import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { cn } from '@/lib/utils';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
const SIMPLE_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

export interface ContactsPaginationProps {
  pageNo: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onPageChange: (pageNo: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  className?: string;
  itemLabel?: string;
  pageSizeOptions?: readonly number[];
  /**
   * Org-wide total ignoring every filter. When it is set and differs from `totalCount`, the count
   * label gains a "(filtered from N)" suffix. Left undefined when nothing was filtered — the
   * backend omits `pageInfo.totalUnfiltered` in that case rather than repeating `total`.
   */
  totalUnfiltered?: number;
  /** `simple` matches the design prototype: rows-per-page + previous/next only. */
  variant?: 'numbered' | 'simple';
  isLoading?: boolean;
  isError?: boolean;
}

const getVisiblePages = (pageNo: number, totalPages: number): (number | 'ellipsis')[] => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (pageNo <= 3) {
    return [1, 2, 3, 'ellipsis', totalPages];
  }

  if (pageNo >= totalPages - 2) {
    return [1, 'ellipsis', totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, 'ellipsis', pageNo - 1, pageNo, pageNo + 1, 'ellipsis', totalPages];
};

export const ContactsPagination = ({
  pageNo,
  pageSize,
  totalCount,
  totalPages,
  hasNextPage,
  hasPreviousPage,
  totalUnfiltered,
  onPageChange,
  onPageSizeChange,
  className,
  itemLabel = 'contacts',
  pageSizeOptions,
  variant = 'numbered',
  isLoading = false,
  isError = false,
}: ContactsPaginationProps) => {
  if (variant !== 'simple' && !isLoading && !isError && (totalCount === 0 || totalPages === 0)) {
    return null;
  }

  const start = totalCount > 0 ? (pageNo - 1) * pageSize + 1 : 0;
  const end = totalCount > 0 ? Math.min(pageNo * pageSize, totalCount) : 0;
  const visiblePages = getVisiblePages(pageNo, totalPages);
  const basePageSizeOptions =
    pageSizeOptions ??
    (variant === 'simple' ? SIMPLE_PAGE_SIZE_OPTIONS : DEFAULT_PAGE_SIZE_OPTIONS);
  const resolvedPageSizeOptions = basePageSizeOptions.includes(pageSize)
    ? basePageSizeOptions
    : [...basePageSizeOptions, pageSize].sort((a, b) => a - b);
  const rowsPerPageLabel = variant === 'simple' ? 'Rows per page' : 'Items per page';
  // Only meaningful when the grand total actually exceeds the filtered total. Equal values would
  // render "of 44 (filtered from 44)", and a smaller unfiltered total can only mean the two counts
  // were measured against different data — in both cases say nothing.
  const filteredFromLabel =
    typeof totalUnfiltered === 'number' && totalUnfiltered > totalCount
      ? ` (filtered from ${totalUnfiltered.toLocaleString()})`
      : '';

  const countLabel = isLoading
    ? `Loading ${itemLabel}…`
    : isError
      ? `Couldn't load ${itemLabel}`
      : totalCount === 0
        ? `Showing 0 of 0${filteredFromLabel}`
        : variant === 'simple'
          ? `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${totalCount.toLocaleString()}${filteredFromLabel}`
          : `Showing ${start.toLocaleString()} to ${end.toLocaleString()} of ${totalCount.toLocaleString()} ${itemLabel}${filteredFromLabel}`;

  const pagerDisabled = isLoading || isError || totalCount === 0;

  const pagerButtons = (
    <div
      className={cn('flex items-center gap-2', pagerDisabled && 'pointer-events-none opacity-40')}
    >
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={cn(
          'h-8 gap-1.5 px-3 text-xs font-medium',
          variant === 'simple' && 'border-neutral-300 text-neutral-700'
        )}
        onClick={() => onPageChange(pageNo - 1)}
        disabled={!hasPreviousPage || pagerDisabled}
        aria-label="Go to previous page"
      >
        <CaretLeftIcon className="size-3.5" />
        {variant === 'simple' ? 'Previous' : null}
      </Button>

      {variant === 'numbered'
        ? visiblePages.map((page, index) =>
            page === 'ellipsis' ? (
              <span
                key={`ellipsis-${index}`}
                className="flex h-8 w-8 items-center justify-center text-muted-foreground"
              >
                …
              </span>
            ) : (
              <Button
                key={page}
                type="button"
                variant="outline"
                size="icon"
                className={cn(
                  'h-8 w-8',
                  page === pageNo
                    ? 'border-primary-600 text-primary-600 hover:bg-primary-50 hover:text-primary-600'
                    : 'border-transparent text-muted-foreground hover:bg-muted'
                )}
                onClick={() => onPageChange(page)}
                aria-label={`Go to page ${page}`}
                aria-current={page === pageNo ? 'page' : undefined}
              >
                {page}
              </Button>
            )
          )
        : null}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className={cn(
          'h-8 gap-1.5 px-3 text-xs font-medium',
          variant === 'simple' && 'border-neutral-300 text-neutral-700'
        )}
        onClick={() => onPageChange(pageNo + 1)}
        disabled={!hasNextPage || pagerDisabled}
        aria-label="Go to next page"
      >
        {variant === 'simple' ? 'Next' : null}
        <CaretRightIcon className="size-3.5" />
      </Button>
    </div>
  );

  if (variant === 'simple') {
    return (
      <div
        className={cn(
          'flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-neutral-200 px-4 py-3 text-sm text-muted-foreground',
          className
        )}
      >
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-xs text-neutral-600">
            {rowsPerPageLabel}
            <Select
              value={String(pageSize)}
              onValueChange={(value) => onPageSizeChange(Number(value))}
            >
              <SelectTrigger
                className="h-8 w-[4.5rem] border-neutral-300 text-xs font-medium text-foreground"
                aria-label={rowsPerPageLabel}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent side="top">
                {resolvedPageSizeOptions.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <span className="text-xs text-neutral-600">{countLabel}</span>
        </div>
        {pagerButtons}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-4 border-t pt-4 text-sm text-muted-foreground',
        className
      )}
    >
      <p>{countLabel}</p>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="whitespace-nowrap">{rowsPerPageLabel}</span>
          <Select
            value={String(pageSize)}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger className="h-8 w-[70px] text-foreground" aria-label={rowsPerPageLabel}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent side="top">
              {resolvedPageSizeOptions.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {pagerButtons}
      </div>
    </div>
  );
};
